import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { Spinner } from 'react-bootstrap';
import { getCodiciOmaggioDaStampare, segnaCodiciOmaggioStampati, getDatiAzienda } from '../services/api';
import '../print.css';

const SITO_URL = 'https://www.modenacinema.it';

// Biglietti dei codici omaggio degli ordini marketplace: 2 per foglio A4, da ritagliare e infilare nel pacco.
function CodiciOmaggioPrint() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [codici, setCodici] = useState([]);
  const [datiAzienda, setDatiAzienda] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logoPronto, setLogoPronto] = useState(false);
  const printTriggered = useRef(false);
  const codiciRef = useRef([]);

  useEffect(() => {
    const carica = async () => {
      try {
        const [codiciRes, aziendaRes] = await Promise.all([getCodiciOmaggioDaStampare(), getDatiAzienda()]);
        const idsParam = searchParams.get('ids');
        const ids = idsParam ? idsParam.split(',').map(Number) : null;
        const elenco = (codiciRes.data || []).filter(c => !ids || ids.includes(c.id));
        codiciRef.current = elenco;
        setCodici(elenco);
        setDatiAzienda(aziendaRes.data);
        if (!aziendaRes.data?.logo_url) setLogoPronto(true);
      } catch (error) {
        console.error('Errore caricamento codici omaggio:', error);
        alert('Errore nel caricamento dei codici omaggio');
      } finally {
        setLoading(false);
      }
    };
    carica();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const dopoStampa = async () => {
      const ids = codiciRef.current.map(c => c.id);
      if (ids.length && window.confirm(`Ho stampato ${ids.length} biglietti: li segno come stampati?`)) {
        try {
          await segnaCodiciOmaggioStampati(ids);
        } catch (error) {
          alert('Impossibile segnare i codici come stampati');
        }
      }
      navigate('/ordini');
    };

    if (!loading && codici.length > 0 && datiAzienda && logoPronto && !printTriggered.current) {
      printTriggered.current = true;
      window.addEventListener('afterprint', dopoStampa, { once: true });
      setTimeout(() => window.print(), 400);
    }
    return () => window.removeEventListener('afterprint', dopoStampa);
  }, [loading, codici.length, datiAzienda, logoPronto, navigate]);

  if (loading) {
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" />
        <p>Preparazione biglietti...</p>
      </div>
    );
  }

  if (codici.length === 0) {
    return (
      <div className="text-center p-5">
        <h3>Nessun codice da stampare</h3>
        <button onClick={() => navigate('/ordini')} className="btn btn-primary mt-3">Torna agli ordini</button>
      </div>
    );
  }

  const pagine = [];
  for (let i = 0; i < codici.length; i += 2) pagine.push(codici.slice(i, i + 2));
  const logoSrc = datiAzienda?.logo_url ? `${process.env.REACT_APP_STATIC_FILES_BASE_URL}${datiAzienda.logo_url}` : null;

  return (
    <div id="printable-area" className="print-container">
      <style>{`
        @page { size: A4 portrait; margin: 0; }
        .omaggio-page { width: 210mm; height: 297mm; display: flex; flex-direction: column; page-break-after: always; break-after: page; }
        .omaggio-page:last-child { page-break-after: auto; break-after: auto; }
        .omaggio-slot { height: 148.5mm; padding: 9mm 12mm; box-sizing: border-box; display: flex; }
        .omaggio-slot + .omaggio-slot { border-top: 1px dashed #999; }
        .omaggio-ticket { flex: 1; border: 2.5px solid #0066cc; border-radius: 8px; padding: 7mm 9mm; display: flex; flex-direction: column; box-sizing: border-box; background: #fff; }
        .omaggio-logo { text-align: center; min-height: 20mm; display: flex; align-items: center; justify-content: center; }
        .omaggio-logo img { max-height: 20mm; max-width: 70mm; object-fit: contain; }
        .omaggio-logo-text { font-size: 22px; font-weight: 800; color: #0066cc; }
        .omaggio-title { text-align: center; font-size: 21px; font-weight: 800; color: #1a1a1a; margin: 3mm 0 1.5mm; line-height: 1.2; }
        .omaggio-text { text-align: center; font-size: 12.5px; color: #333; line-height: 1.4; margin: 0 6mm; }
        .omaggio-offer { text-align: center; margin: 3.5mm 0 2.5mm; color: #0066cc; font-weight: 800; line-height: 1; }
        .omaggio-offer .grande { font-size: 50px; }
        .omaggio-offer .piccolo { display: block; font-size: 15px; margin-top: 1.5mm; color: #333; font-weight: 700; }
        .omaggio-bottom { display: flex; align-items: center; justify-content: center; gap: 9mm; margin-top: auto; }
        .omaggio-code-label { font-size: 10px; color: #666; text-transform: uppercase; letter-spacing: .6px; }
        .omaggio-code { font-family: 'Courier New', monospace; font-size: 25px; font-weight: 800; color: #000; background: #f5f5f5; border: 2px dashed #888; border-radius: 4px; padding: 2.5mm 5mm; display: inline-block; letter-spacing: 1px; }
        .omaggio-how { font-size: 10.5px; color: #444; margin-top: 2mm; }
        .omaggio-qr { text-align: center; font-size: 9px; color: #555; }
        .omaggio-qr svg { display: block; margin: 0 auto 1mm; }
        .omaggio-small { text-align: center; font-size: 9px; color: #777; margin-top: 3mm; border-top: 1px solid #ddd; padding-top: 2mm; }
        .omaggio-rif { font-size: 8px; color: #999; margin-top: 1mm; }
        @media print { * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; } }
      `}</style>

      {pagine.map((coppia, i) => (
        <div key={i} className="omaggio-page">
          {coppia.map(c => {
            const spedizione = c.tipo === 'SPEDIZIONE_GRATUITA';
            const nome = (c.nome_destinatario || '').trim().split(/\s+/)[0];
            const scadenza = c.valido_fino ? new Date(c.valido_fino).toLocaleDateString('it-IT') : null;
            return (
              <div key={c.id} className="omaggio-slot">
                <div className="omaggio-ticket">
                  <div className="omaggio-logo">
                    {logoSrc ? (
                      <img src={logoSrc} alt={datiAzienda?.denominazione || 'Logo'}
                        onLoad={() => setLogoPronto(true)} onError={() => setLogoPronto(true)} />
                    ) : (
                      <span className="omaggio-logo-text">{datiAzienda?.denominazione || 'MODENA CINEMA'}</span>
                    )}
                  </div>

                  <div className="omaggio-title">
                    {spedizione
                      ? `${nome ? `${nome}, che` : 'Che'} piacere rivederti!`
                      : `Grazie${nome ? `, ${nome}` : ''}! Un regalo per il tuo prossimo film`}
                  </div>
                  <div className="omaggio-text">
                    {spedizione
                      ? 'Per ringraziarti di aver già scelto modenacinema.it, sul tuo prossimo ordine la spedizione la offriamo noi.'
                      : 'Su modenacinema.it trovi migliaia di DVD, Blu-ray e 4K, con novità ogni settimana e prezzi che non ti aspetti. Passa a trovarci: questo codice è solo per te.'}
                  </div>

                  <div className="omaggio-offer">
                    {spedizione ? (
                      <><span className="grande">Spedizione gratis</span><span className="piccolo">sul tuo prossimo ordine in Italia</span></>
                    ) : (
                      <><span className="grande">-{c.percentuale_sconto}%</span><span className="piccolo">sul tuo primo acquisto</span></>
                    )}
                  </div>

                  <div className="omaggio-bottom">
                    <div style={{ textAlign: 'center' }}>
                      <div className="omaggio-code-label">Il tuo codice</div>
                      <div className="omaggio-code">{c.codice}</div>
                      <div className="omaggio-how">Inseriscilo al carrello su <strong>modenacinema.it</strong></div>
                    </div>
                    <div className="omaggio-qr">
                      <QRCodeSVG value={`${SITO_URL}/?codice=${encodeURIComponent(c.codice)}`} size={92} level="M" />
                      Inquadra e vai al sito
                    </div>
                  </div>

                  <div className="omaggio-small">
                    Codice personale monouso{scadenza ? `, valido fino al ${scadenza}` : ''}. Non cumulabile con altri codici sconto.
                    <div className="omaggio-rif">
                      Rif. ordine #{c.ordine_id}{c.numero_ordine_marketplace ? ` · ${c.origine || ''} ${c.numero_ordine_marketplace}` : ''}{c.cliente_nome ? ` · ${c.cliente_nome}` : ''}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

export default CodiciOmaggioPrint;
